import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { isActivityPubliclyVisible } from "@/lib/events/publicVisibility";
import { fetchCourseOccurrenceInfo, resolveCourseVisibilityRow } from "@/lib/events/resolveCourseOccurrences";
import { getNextUpcomingOccurrence } from "@/lib/fleaMarkets/occurrences";

/**
 * Fetch upcoming public events for the Life Signal landing section.
 *
 * Logic:
 * 1. date_time events where start_date >= now
 * 2. open/season events where end_date >= now or end_date is null
 * 3. If < 3 results, include anytime events (legacy)
 * 4. Sort by nearest start_date
 * 5. Max 6
 *
 * Also fetches outing_tags for experience filtering and resolves
 * area name via producer → place chain.
 */
export interface LandingEvent {
  id: string;
  kind: "activity" | "flea_market";
  title: string;
  event_type: string;
  timing_mode: string;
  start_date: string | null;
  end_date: string | null;
  custom_address: string | null;
  use_producer_address: boolean;
  producer_id: string;
  producer_name: string | null;
  occurrences_count: number;
  is_recurring?: boolean;
  outing_tags: string[];
  area_name: string | null;
  place_name: string | null;
  booking_mode: string;
  href: string;
  cover_owner_type: "event" | "flea_market";
  cover_owner_id: string;
}

const SELECT_COLS = `
  id, title, event_type, timing_mode, start_date, end_date, is_recurring,
  custom_address, use_producer_address, producer_id, outing_tags, booking_mode,
  producers!producer_events_producer_id_fkey ( name )
`;

interface LandingEventRow {
  id: string;
  title: string;
  event_type: string;
  timing_mode: string;
  start_date: string | null;
  end_date: string | null;
  is_recurring: boolean | null;
  custom_address: string | null;
  use_producer_address: boolean | null;
  producer_id: string;
  outing_tags: string[] | null;
  booking_mode: string | null;
  producers?: { name: string | null } | null;
}

export function useLandingEvents(max = 6) {
  return useQuery<LandingEvent[]>({
    queryKey: ["landing-events", max],
    queryFn: async () => {
      const now = new Date().toISOString();

      // Fetch date_time + open + season events that are upcoming
      const { data: upcoming, error: err1 } = await supabase
        .from("producer_events")
        .select(SELECT_COLS)
        .eq("status", "public")
        .in("timing_mode", ["date_time", "open", "season"])
        .or(`start_date.gte.${now},end_date.gte.${now},end_date.is.null`)
        .order("start_date", { ascending: true })
        .limit(max);

      if (err1) {
        console.error("useLandingEvents upcoming:", err1);
        return [];
      }

      // Map rows first, then resolve courses, then apply visibility filter.
      // Course start_date is the FIRST occurrence (possibly past); we must
      // resolve next occurrence before visibility filtering.
      const nowDate = new Date();
      let results: LandingEvent[] = (upcoming ?? []).map((row) => mapRow(row as LandingEventRow));

      // If fewer than 3 date_time/season results, backfill with anytime events
      if (results.length < 3) {
        const existingIds = results.map((r) => r.id);
        const { data: anytime } = await supabase
          .from("producer_events")
          .select(SELECT_COLS)
          .eq("status", "public")
          .eq("timing_mode", "anytime")
          .not("id", "in", `(${existingIds.join(",")})`)
          .order("created_at", { ascending: false })
          .limit(max - results.length);

        if (anytime) {
          results = [...results, ...anytime.map((row) => mapRow(row as LandingEventRow))];
        }
      }

      // For courses: resolve next upcoming occurrence BEFORE visibility filter.
      const courseIds = results.filter(r => r.event_type === 'course').map(r => r.id);
      if (courseIds.length > 0) {
        const courseInfo = await fetchCourseOccurrenceInfo(courseIds);
        results = results
          .map((row) => resolveCourseVisibilityRow(row, courseInfo.get(row.id)))
          .filter((row): row is LandingEvent => row !== null);
      }

      // NOW apply canonical visibility filter with resolved dates
      results = results.filter(ev =>
        isActivityPubliclyVisible({
          timingMode: ev.timing_mode,
          startDate: ev.start_date,
          endDate: ev.end_date,
        }, nowDate)
      );

      // Resolve area names via producer → place_producers_public → places_explore
      const producerIds = [...new Set(results.map(r => r.producer_id))];
      if (producerIds.length > 0) {
        const { data: ppRows } = await supabase
          .rpc("get_place_producers_public" as any, {
            p_producer_ids: producerIds,
            p_limit: Math.max(producerIds.length * 3, 20),
          })
          .select("producer_id, place_id") as { data: any[] | null };

        if (ppRows && ppRows.length > 0) {
          const placeIds = [...new Set(ppRows.map(r => r.place_id).filter(Boolean))] as string[];
          if (placeIds.length > 0) {
            const { data: placeRows } = await supabase
              .from("places_explore")
              .select("id, primary_area_name")
              .in("id", placeIds);

            if (placeRows) {
              // Build producer_id → area_name map
              const placeAreaMap = new Map(placeRows.map(p => [p.id, p.primary_area_name]));
              const producerAreaMap = new Map<string, string | null>();
              for (const pp of ppRows) {
                if (pp.place_id && placeAreaMap.has(pp.place_id)) {
                  producerAreaMap.set(pp.producer_id!, placeAreaMap.get(pp.place_id) ?? null);
                }
              }
              for (const r of results) {
                r.area_name = producerAreaMap.get(r.producer_id) ?? null;
              }
            }
          }
        }
      }

      const fleaMoments = await fetchLandingFleaMoments(max, now);

      return [...results, ...fleaMoments]
        .sort((left, right) => {
          const leftTs = left.start_date ? new Date(left.start_date).getTime() : Number.POSITIVE_INFINITY;
          const rightTs = right.start_date ? new Date(right.start_date).getTime() : Number.POSITIVE_INFINITY;
          return leftTs - rightTs;
        })
        .slice(0, max);
    },
    staleTime: 5 * 60 * 1000,
  });
}

async function fetchLandingFleaMoments(max: number, nowIso: string): Promise<LandingEvent[]> {
  const { data: marketsData, error: marketsError } = await supabase
    .from("producer_flea_markets")
    .select("id, title, place_id, producer_id")
    .eq("status", "public");

  if (marketsError) {
    console.error("useLandingEvents flea markets:", marketsError);
    return [];
  }

  const markets = (marketsData ?? []) as Array<{
    id: string;
    title: string;
    place_id: string;
    producer_id: string;
  }>;

  if (markets.length === 0) return [];

  const marketIds = markets.map((market) => market.id);
  const placeIds = [...new Set(markets.map((market) => market.place_id).filter(Boolean))] as string[];
  const producerIds = [...new Set(markets.map((market) => market.producer_id).filter(Boolean))] as string[];

  const [{ data: occurrenceRows, error: occurrenceError }, { data: placeRows }, { data: producerRows }] = await Promise.all([
    supabase
      .from("producer_flea_market_occurrences")
      .select("flea_market_id, start_at, end_at, status")
      .in("flea_market_id", marketIds)
      .eq("status", "scheduled")
      .gte("start_at", nowIso)
      .order("start_at", { ascending: true }),
    supabase
      .from("places")
      .select("id, name, landskap, municipality")
      .in("id", placeIds),
    supabase
      .from("producers")
      .select("id, name")
      .in("id", producerIds),
  ]);

  if (occurrenceError) {
    console.error("useLandingEvents flea occurrences:", occurrenceError);
    return [];
  }

  const placesById = new Map(
    ((placeRows ?? []) as Array<{ id: string; name: string | null; landskap: string | null; municipality: string | null }>).map((place) => [place.id, place]),
  );
  const producersById = new Map(
    ((producerRows ?? []) as Array<{ id: string; name: string | null }>).map((producer) => [producer.id, producer]),
  );

  const occurrencesByMarketId = new Map<string, Array<{ start_at: string; end_at: string | null; status?: string }>>();
  ((occurrenceRows ?? []) as Array<{ flea_market_id: string; start_at: string; end_at: string | null; status?: string }>).forEach((row) => {
    const rows = occurrencesByMarketId.get(row.flea_market_id) ?? [];
    rows.push({ start_at: row.start_at, end_at: row.end_at, status: row.status });
    occurrencesByMarketId.set(row.flea_market_id, rows);
  });

  const moments = markets
    .map((market) => {
      const nextOccurrence = getNextUpcomingOccurrence(occurrencesByMarketId.get(market.id) ?? []);
      if (!nextOccurrence) return null;

      const place = placesById.get(market.place_id);
      const producer = producersById.get(market.producer_id);

      return {
        id: market.id,
        kind: "flea_market" as const,
        title: market.title,
        event_type: "flea_market",
        timing_mode: "date_time",
        start_date: nextOccurrence.start_at,
        end_date: nextOccurrence.end_at ?? nextOccurrence.start_at,
        custom_address: null,
        use_producer_address: true,
        producer_id: market.producer_id,
        producer_name: producer?.name ?? null,
        occurrences_count: 0,
        outing_tags: [],
        area_name: place?.landskap ?? place?.municipality ?? null,
        place_name: place?.name ?? null,
        booking_mode: "none",
        href: `/loppis/${market.id}`,
        cover_owner_type: "flea_market" as const,
        cover_owner_id: market.id,
      } as LandingEvent;
    })
    .filter((item): item is LandingEvent => item !== null)
    .sort((left, right) => new Date(left.start_date ?? 0).getTime() - new Date(right.start_date ?? 0).getTime())
    .slice(0, max);

  return moments;
}

function mapRow(row: LandingEventRow): LandingEvent {
  return {
    id: row.id,
    kind: "activity",
    title: row.title,
    event_type: row.event_type,
    timing_mode: row.timing_mode,
    start_date: row.start_date,
    end_date: row.end_date,
    is_recurring: row.is_recurring ?? false,
    custom_address: row.custom_address,
    use_producer_address: row.use_producer_address ?? true,
    producer_id: row.producer_id,
    producer_name: row.producers?.name ?? null,
    occurrences_count: 0,
    outing_tags: row.outing_tags ?? [],
    area_name: null, // resolved after fetch
    place_name: null,
    booking_mode: row.booking_mode ?? 'none',
    href: `/event/${row.id}`,
    cover_owner_type: "event",
    cover_owner_id: row.id,
  };
}
